//import liraries
import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardMedia, Chip, Stack, useTheme, useMediaQuery } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { useNavigate } from 'react-router-dom';
import CategoryChips from '../../Components/CategoryChips';
import { calculateDistance } from '../../Utils/googleMapsDistance';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// create a component
const DashboardPlaceScreen: React.FC<{ title: string ,noCategories?: boolean}> = ({ title,noCategories }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  
  // Get the new places array from the dashboard slice
  const newPlaces = useSelector((state: RootState) => state.dashboard.curatedPlaces);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const userLocation = useSelector((state: RootState) => state.location.location);
  const categories = useSelector((state: RootState) => state.dashboard.categories);

  const [modalOpen, setModalOpen] = useState(false);
  
  // Calculate maxInitialDisplay based on screen width: xs (3), sm (5), md+ (8)
  const maxInitialDisplay = isMd ? 8 : isXs ? 3 : 5;
  // Helper to decide colour of the rating pill (same logic as PlaceDetail)
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return '#2e7d32'; // Green for 4+
    if (rating >= 3) return '#ed6c02'; // Orange for 3-3.9
    return '#d32f2f'; // Red for <3
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'row', mb: 1 }}>
      <Box onClick={() => navigate('/allplaces')} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography  fontWeight={500} sx={{fontSize: '16px', cursor: 'pointer'}}>{title}</Typography>
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
      </Box>
        {newPlaces?.length > 4 && !noCategories && (
          <Typography
            sx={{ ml: 'auto', cursor: 'pointer' }}
            onClick={() => setModalOpen(!modalOpen)}
          >
            View All
          </Typography>
        )}
      </Box>
      {!noCategories && (
        <CategoryChips
        noTitle
        isSeeMoreVisibel={false}
        categories={categories}
        selectedCategories={selectedCategories}
        onChange={(newCategories) => {
          setSelectedCategories(newCategories.map(category => category.name));
        }}
        onChipClick={(categoryName, isSelected) => {
          if (isSelected) {
            navigate('/allplaces', { state: { category: categoryName } });
          }
        }}
          maxInitialDisplay={maxInitialDisplay}
          openModal={modalOpen}
          onModalClose={() => setModalOpen(false)}
        />
      )}

      {/* Horizontal scroll list */}
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: 2,
          pb: 2,
          mt: 2,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari and Opera
          },
        }}
      >
        {newPlaces?.map((place: any) => {
          const imageUrl = place?.files?.[0]?.fileURL || '';
          const rating = place?.averageRating ?? 0;
          const tags: string[] = place?.tags ?? [];
          const placeCategories: string[] = place?.categories ?? [];
          
          // Create a component for each place to handle async distance calculation
          const PlaceCard = () => {
            const [distance, setDistance] = useState<number | null>(null);
            
            useEffect(() => {
              if (userLocation?.lat && userLocation?.lng && place?.location?.lat && place?.location?.lng) {
                calculateDistance(userLocation.lat, userLocation.lng, place.location.lat, place.location.lng)
                  .then(result => setDistance(result))
                  .catch(() => setDistance(null));
              }
            }, [userLocation, place]);

            return (
            <Card
              key={place._id}
              sx={{
                flex: '0 0 60%', // roughly two cards per viewport
                minWidth: 180,
                borderRadius: 4,
                boxShadow: 3,
                overflow: 'hidden',
              }}
              onClick={() => navigate(`/places/${place._id}`)}
            >
              {/* Image with tag overlays */}
              <Box sx={{ position: 'relative' }}>
                <CardMedia component="img" height="160" image={imageUrl} alt={place.name} />
                {tags?.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                    }}
                  >
                    {tags.slice(0, 2).map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.7)',
                          backdropFilter: 'blur(4px)',
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Stack>
                )}
                {/* Category at bottom left */}
                {placeCategories?.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      left: 8,
                    }}
                  >
                    <Chip
                      label={placeCategories[0]}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(1px)',
                        color: 'white',
                        fontWeight: 500,
                        border: '1px solid #CAC4D0',
                        borderRadius: '6px',
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Details section */}
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: 'white',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="bold" 
                    noWrap
                  >
                    {place.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {distance ? `${distance}km` : 'N/A'}
                  </Typography>
                </Box>

                {/* Rating pill */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: getRatingColor(rating),
                    color: 'white',
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1 }}>
                    {rating}
                  </Typography>
                  <StarIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            </Card>
            );
          };

          return <PlaceCard key={place._id} />;
        })}
      </Box>
    </Box>
  );
};

//make this component available to the app
export default DashboardPlaceScreen;
